import { HiDotsVertical } from "react-icons/hi";
import { useRef, useEffect } from "react";
import ActionMenu from "@components/menus/ActionMenu";

export default function PatientCard({
  name,
  phone,
  index,
  isOpen,
  onToggle,
  closeMenu,
  onClick,
  onDelete,
}) {
  const menuRef = useRef();
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeMenu]);

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    onToggle();
  };

  // Generate consistent avatar color based on name
  const getAvatarGradient = (name) => {
    const colors = [
      ['#14b8a6', '#0d9488'], // teal
      ['#6366f1', '#4f46e5'], // indigo
      ['#8b5cf6', '#7c3aed'], // violet
      ['#ec4899', '#db2777'], // pink
      ['#f59e0b', '#d97706'], // amber
      ['#10b981', '#059669'], // emerald
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const [gradientFrom, gradientTo] = getAvatarGradient(name);

  return (
    <div
      onClick={onClick}
      className="dashboard-card group p-5 cursor-pointer w-full animate-fade-in-up"
      style={{
        opacity: 0,
        animationDelay: `${index * 0.05}s`,
        animationFillMode: 'forwards'
      }}
    >
      <div className="flex items-center gap-4">
        {/* Avatar */}
        <div
          className="relative flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white font-semibold text-sm shadow-lg group-hover:scale-105 transition-transform duration-300"
          style={{
            background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
            fontFamily: 'var(--font-display)'
          }}
        >
          {initials}
          {/* Subtle shine effect */}
          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/20 to-transparent"></div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p
            className="font-semibold text-slate-800 truncate group-hover:text-slate-900 transition-colors"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {name}
          </p>
          <div className="flex items-center gap-1.5 mt-1">
            <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <p className="text-sm text-slate-500 truncate" style={{ fontFamily: 'var(--font-body)' }}>
              {phone || 'No phone'}
            </p>
          </div>
        </div>

        {/* Menu button */}
        <div
          className="relative flex-shrink-0"
          ref={menuRef}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleMenuToggle}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all duration-200 opacity-0 group-hover:opacity-100"
          >
            <HiDotsVertical className="w-5 h-5" />
          </button>
          {isOpen && <ActionMenu index={index} onDelete={onDelete} />}
        </div>
      </div>

      {/* Bottom accent line (appears on hover) */}
      <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
    </div>
  );
}
